/**
 * components/email/PasswordField.tsx — mailbox password input with a
 * strength meter, a live rule checklist driven by GET /password-policy
 * (falls back to the local defaults when the request fails), a
 * "Generate strong password" button (crypto.getRandomValues, 16 chars,
 * all four classes) and copy.
 *
 * `checkPassword()` is exported so bulk importers can flag weak rows with
 * the same rules the server enforces.
 */
import React, { useEffect, useId, useMemo, useState } from 'react';
import { fetchPasswordPolicy, PasswordPolicy, useCopy } from './emailApi';

export const DEFAULT_POLICY: PasswordPolicy = {
  min_length: 12,
  max_length: 128,
  classes_required: 3,
  rules: [
    'At least 12 characters',
    'At least 3 of: lowercase, uppercase, digits, symbols',
    'Not the mailbox address or an obvious word',
  ],
};

export interface PasswordCheck {
  ok: boolean;
  /** 0..4 — drives the meter. */
  score: number;
  label: 'Too weak' | 'Weak' | 'Fair' | 'Strong' | 'Very strong';
  classes: { lower: boolean; upper: boolean; digit: boolean; symbol: boolean };
  classCount: number;
  checks: Array<{ id: string; label: string; ok: boolean }>;
  problems: string[];
}

const COMMON = ['password', 'passw0rd', 'qwerty', '123456', 'letmein', 'welcome', 'admin', 'iloveyou', 'abc123', 'monkey', 'dragon', 'master', 'login', 'email'];

/** Pure check used by the field and by bulk preflight. */
export function checkPassword(pw: string, policy: PasswordPolicy = DEFAULT_POLICY, context: { email?: string; localPart?: string } = {}): PasswordCheck {
  const p = pw || '';
  const classes = {
    lower: /[a-z]/.test(p),
    upper: /[A-Z]/.test(p),
    digit: /[0-9]/.test(p),
    symbol: /[^A-Za-z0-9]/.test(p),
  };
  const classCount = Object.values(classes).filter(Boolean).length;
  const min = Number(policy.min_length) || DEFAULT_POLICY.min_length;
  const max = Number(policy.max_length) || DEFAULT_POLICY.max_length;
  const need = Number(policy.classes_required) || DEFAULT_POLICY.classes_required;

  const lowered = p.toLowerCase();
  const local = (context.localPart || (context.email || '').split('@')[0] || '').toLowerCase();
  const containsIdentity = !!local && local.length >= 3 && lowered.includes(local);
  const isCommon = COMMON.some(w => lowered === w || (lowered.length <= w.length + 3 && lowered.includes(w)));
  const repeated = /(.)\1{3,}/.test(p);

  const checks = [
    { id: 'len', label: `At least ${min} characters`, ok: p.length >= min },
    { id: 'max', label: `No more than ${max} characters`, ok: p.length <= max },
    { id: 'classes', label: `At least ${need} of: lowercase, uppercase, digits, symbols`, ok: classCount >= need },
    { id: 'identity', label: 'Not the mailbox name or a common word', ok: !containsIdentity && !isCommon },
  ];
  const problems = checks.filter(c => !c.ok).map(c => c.label);
  if (repeated) problems.push('Avoid long runs of the same character');

  // Strength: length + variety, penalised for the obvious.
  let score = 0;
  if (p.length >= min) score += 1;
  if (p.length >= min + 4) score += 1;
  if (classCount >= need) score += 1;
  if (classCount === 4 && p.length >= 14) score += 1;
  if (containsIdentity || isCommon || repeated) score = Math.min(score, 1);
  if (p.length === 0) score = 0;

  const ok = checks.every(c => c.ok);
  const labels: PasswordCheck['label'][] = ['Too weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
  return { ok, score, label: labels[score], classes, classCount, checks, problems };
}

const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGIT = '23456789';
const SYMBOL = '!@#$%^&*-_=+?';
const ALL = LOWER + UPPER + DIGIT + SYMBOL;

function randomInts(n: number, max: number): number[] {
  const out: number[] = [];
  const cryptoObj: Crypto | undefined = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObj && cryptoObj.getRandomValues) {
    const buf = new Uint32Array(n);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < n; i++) out.push(buf[i] % max);
  } else {
    for (let i = 0; i < n; i++) out.push(Math.floor(Math.random() * max));
  }
  return out;
}

/** 16 characters, guaranteed to contain all four classes. */
export function generatePassword(length = 16): string {
  const len = Math.max(12, length);
  const picks = randomInts(len, ALL.length).map(i => ALL[i]);
  // Force one of each class into random positions.
  const forced = [LOWER, UPPER, DIGIT, SYMBOL].map((set, i) => set[randomInts(1, set.length)[0]]);
  const positions = randomInts(4, len);
  const used = new Set<number>();
  forced.forEach((ch, i) => {
    let pos = positions[i];
    while (used.has(pos)) pos = (pos + 1) % len;
    used.add(pos);
    picks[pos] = ch;
  });
  return picks.join('');
}

/** Loads the server policy once per domain; falls back locally. */
export function usePasswordPolicy(domainRowId?: string | null) {
  const [policy, setPolicy] = useState<PasswordPolicy>(DEFAULT_POLICY);
  const [source, setSource] = useState<'server' | 'local'>('local');
  useEffect(() => {
    if (!domainRowId) return;
    let alive = true;
    fetchPasswordPolicy(domainRowId)
      .then(p => {
        if (!alive || !p) return;
        setPolicy({
          min_length: Number(p.min_length) || DEFAULT_POLICY.min_length,
          max_length: Number(p.max_length) || DEFAULT_POLICY.max_length,
          classes_required: Number(p.classes_required) || DEFAULT_POLICY.classes_required,
          rules: Array.isArray(p.rules) && p.rules.length ? p.rules : DEFAULT_POLICY.rules,
        });
        setSource('server');
      })
      .catch(() => { /* local fallback is already in place */ });
    return () => { alive = false; };
  }, [domainRowId]);
  return { policy, source };
}

const meterClass = (score: number) =>
  score >= 3 ? 'bg-secondary-500' : score === 2 ? 'bg-secondary-300' : score === 1 ? 'bg-secondary-200' : 'bg-gray-200';

export const PasswordField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  policy?: PasswordPolicy;
  /** For the "not the mailbox name" rule. */
  email?: string;
  localPart?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  /** Hide the checklist until the user types (edit modal). */
  quietWhenEmpty?: boolean;
  className?: string;
}> = ({
  value, onChange, policy = DEFAULT_POLICY, email, localPart, id, label = 'Password', placeholder,
  required, disabled, autoComplete = 'new-password', quietWhenEmpty = false, className = '',
}) => {
  const autoId = useId();
  const inputId = id || `pw-${autoId}`;
  const [show, setShow] = useState(false);
  const { copied, copy } = useCopy();
  const check = useMemo(() => checkPassword(value, policy, { email, localPart }), [value, policy, email, localPart]);
  const showDetails = !(quietWhenEmpty && !value);

  const generate = () => {
    const pw = generatePassword(16);
    onChange(pw);
    setShow(true);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label htmlFor={inputId} className="block text-xs uppercase tracking-wider text-gray-500">{label}</label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={generate} disabled={disabled} className="text-xs text-primary-navy underline disabled:opacity-50">
            Generate strong password
          </button>
          {value && (
            <button type="button" onClick={() => copy(value, 'Password')} disabled={disabled} className="text-xs text-primary-navy underline disabled:opacity-50">
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </div>
      <div className="flex">
        <input
          id={inputId}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || `at least ${policy.min_length} characters`}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          spellCheck={false}
          aria-describedby={`${inputId}-rules`}
          aria-invalid={value ? !check.ok : undefined}
          className="w-full px-3 py-2 border border-gray-300 rounded-l-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          disabled={disabled}
          aria-pressed={show}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="px-3 border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 text-xs text-primary-navy hover:bg-gray-100 disabled:opacity-50"
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
      <span className="sr-only" aria-live="polite">{copied ? 'Password copied to clipboard' : ''}</span>

      {showDetails && (
        <div id={`${inputId}-rules`} className="mt-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex gap-1" aria-hidden>
              {[1, 2, 3, 4].map(i => (
                <div key={i} className={`h-1.5 flex-1 rounded ${i <= check.score ? meterClass(check.score) : 'bg-gray-100'}`} />
              ))}
            </div>
            <span className="text-xs text-primary-navy w-24 text-right" role="status" aria-live="polite">
              {value ? check.label : ''}
            </span>
          </div>
          <ul className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {check.checks.map(c => (
              <li key={c.id} className={c.ok ? 'text-primary-navy' : 'text-gray-500'}>
                <span aria-hidden className="inline-block w-4">{c.ok ? '✓' : '○'}</span>
                <span className="sr-only">{c.ok ? 'met: ' : 'not met: '}</span>
                {c.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordField;
